import React, { useState, useEffect, useCallback } from 'react';
import { FaSearch, FaTimes, FaRoute, FaMountain, FaCalendarAlt, FaSun, FaSnowflake, FaLeaf, FaTree } from 'react-icons/fa';
import { EnhancedTrailData, Season } from '@/types/enhancedTrail';
import { useLanguage } from '@/context/LanguageContext';

interface TrailSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  trails: EnhancedTrailData[];
  onTrailSelect: (trail: EnhancedTrailData) => void;
  currentSeason?: Season;
}

export default function TrailSearchModal({
  isOpen,
  onClose,
  trails,
  onTrailSelect,
  currentSeason = 'summer'
}: TrailSearchModalProps) {
  const { translate } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<EnhancedTrailData[]>([]);
  const [selectedTrail, setSelectedTrail] = useState<EnhancedTrailData | null>(null);

  // Reset search when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setSearchResults([]);
      setSelectedTrail(null);
    }
  }, [isOpen]);

  // Intelligent search algorithm
  const searchTrails = useCallback((term: string) => {
    if (!term.trim()) {
      setSearchResults([]);
      return;
    }

    const normalizedTerm = term.toLowerCase().trim();
    const terms = normalizedTerm.split(/\s+/);

    // Score-based search for more intelligent results
    const scoredResults = trails.map(trail => {
      let score = 0;

      // Check name (highest weight)
      if (trail.name.toLowerCase().includes(normalizedTerm)) {
        score += 10;
      }

      // Check individual terms in name
      terms.forEach(term => {
        if (trail.name.toLowerCase().includes(term)) {
          score += 5;
        }
      });

      // Check description
      if (trail.description?.toLowerCase().includes(normalizedTerm)) {
        score += 3;
      }

      // Check individual terms in description
      terms.forEach(term => {
        if (trail.description?.toLowerCase().includes(term)) {
          score += 1;
        }
      });

      // Check tags
      trail.tags?.forEach(tag => {
        if (tag.toLowerCase().includes(normalizedTerm)) {
          score += 4;
        }

        terms.forEach(term => {
          if (tag.toLowerCase().includes(term)) {
            score += 2;
          }
        });
      });

      // Check difficulty
      if (trail.difficulty && trail.difficulty.toLowerCase().includes(normalizedTerm)) {
        score += 3;
      }

      // Check region with enhanced weighting for location-based searches
      if (trail.region?.toLowerCase().includes(normalizedTerm)) {
        score += 5; // Increased from 3 to give more weight to region matches
      }

      // Special handling for location-based multi-word searches
      if (terms.length > 1) {
        // Check if multiple terms appear in region (like "Engelberg Wolfenschiesen")
        let regionMatchCount = 0;
        terms.forEach(term => {
          if (trail.region?.toLowerCase().includes(term)) {
            regionMatchCount++;
          }
        });

        // If multiple location terms match, give a significant boost
        if (regionMatchCount > 1) {
          score += regionMatchCount * 4;
        }

        // Check for partial location name matches (e.g., "Wolfen" should match "Wolfenschiesen")
        terms.forEach(term => {
          if (term.length > 3) { // Only consider substantial terms (avoid "in", "at", etc.)
            const cities = ["engelberg", "wolfenschiesen", "grafenort", "dallenwil", "stans"];
            cities.forEach(city => {
              // If the term is part of a known city name or vice versa
              if (city.includes(term) || term.includes(city)) {
                if (trail.region?.toLowerCase().includes(city)) {
                  score += 3;
                }
              }
            });
          }
        });
      }

      // Check for seasonal keywords
      const seasonTerms = {
        'winter': ['winter', 'snow', 'cold', 'christmas', 'december', 'january', 'february'],
        'spring': ['spring', 'bloom', 'flower', 'march', 'april', 'may'],
        'summer': ['summer', 'hot', 'warm', 'june', 'july', 'august'],
        'autumn': ['autumn', 'fall', 'leaves', 'foliage', 'september', 'october', 'november'],
      };

      Object.entries(seasonTerms).forEach(([season, keywords]) => {
        keywords.forEach(keyword => {
          if (normalizedTerm.includes(keyword)) {
            // If trail is good for that season, boost score
            if (trail.bestSeasons?.includes(season as Season)) {
              score += 3;
            }
          }
        });
      });

      // Boost score for trails that are good in current season
      if (trail.bestSeasons?.includes(currentSeason)) {
        score += 1;
      }

      // Check for distance-related terms
      if (/short|brief|quick/i.test(normalizedTerm) && trail.totalDistance && trail.totalDistance < 5) {
        score += 3;
      }
      if (/long|lengthy|extended/i.test(normalizedTerm) && trail.totalDistance && trail.totalDistance > 10) {
        score += 3;
      }

      // Check for difficulty-related terms
      if (/easy|simple|beginner/i.test(normalizedTerm) && trail.difficulty === 'easy') {
        score += 3;
      }
      if (/moderate|intermediate/i.test(normalizedTerm) && trail.difficulty === 'moderate') {
        score += 3;
      }
      if (/difficult|hard|challenging/i.test(normalizedTerm) && trail.difficulty === 'difficult') {
        score += 3;
      }
      if (/extreme|expert/i.test(normalizedTerm) && trail.difficulty === 'extreme') {
        score += 3;
      }

      // Check for family-friendly terms
      if (/family|kid|child/i.test(normalizedTerm) && trail.familySuitability && trail.familySuitability >= 7) {
        score += 4;
      }

      // Return trail with its search score
      return { trail, score };
    });

    // Filter trails with a score > 0 and sort by score (highest first)
    const filteredResults = scoredResults
      .filter(result => result.score > 0)
      .sort((a, b) => b.score - a.score)
      .map(result => result.trail);

    setSearchResults(filteredResults);
  }, [trails, currentSeason]);

  // Handle search input changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    searchTrails(value);
  };

  // Handle trail selection
  const handleTrailClick = (trail: EnhancedTrailData) => {
    setSelectedTrail(trail);
  };

  // Handle confirmation
  const handleConfirm = () => {
    if (selectedTrail) {
      onTrailSelect(selectedTrail);
      onClose();
    }
  };

  // If modal is not open, don't render anything
  if (!isOpen) return null;

  // Render seasonal icon based on season
  const renderSeasonIcon = (season: Season) => {
    switch (season) {
      case 'spring': return <FaLeaf className="text-green-500" />;
      case 'summer': return <FaSun className="text-yellow-500" />;
      case 'autumn': return <FaTree className="text-orange-500" />;
      case 'winter': return <FaSnowflake className="text-blue-400" />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold">
            {translate('trailDiscovery.searchTrails', 'Search Trails')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* Search input */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder={translate('trailDiscovery.searchPlaceholder', 'Search by name, difficulty, region, or features...')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
          </div>
          <p className="mt-2 text-xs text-gray-500">
            {translate('trailDiscovery.searchHelp', 'Try search terms like "easy family trail", "summer view", "short hike" or specific names')}
          </p>
        </div>

        {/* Results area */}
        <div className="flex-1 overflow-y-auto p-2">
          {searchResults.length === 0 && searchTerm ? (
            <div className="p-4 text-center text-gray-500">
              {translate('trailDiscovery.noResults', 'No trails found matching your search')}
            </div>
          ) : searchTerm ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-2">
              {searchResults.map(trail => (
                <div
                  key={trail.id}
                  onClick={() => handleTrailClick(trail)}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${selectedTrail?.id === trail.id
                    ? 'bg-blue-50 border-blue-300'
                    : 'hover:bg-gray-50 border-gray-200'
                    }`}
                >
                  <div className="flex items-start">
                    <div className="mr-3">
                      <FaRoute className={`text-xl ${selectedTrail?.id === trail.id ? 'text-blue-500' : 'text-gray-500'
                        }`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium">{trail.name}</h3>
                      <div className="flex flex-wrap gap-2 mt-1">
                        <span className="inline-flex items-center px-2 py-0.5 text-xs bg-gray-100 text-gray-800 rounded">
                          <FaMountain className="mr-1 text-gray-600" />
                          {trail.difficulty}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 text-xs bg-gray-100 text-gray-800 rounded">
                          {trail.totalDistance ? `${trail.totalDistance.toFixed(1)} km` : 'N/A'}
                        </span>
                        {trail.bestSeasons && trail.bestSeasons.length > 0 && (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs bg-gray-100 text-gray-800 rounded">
                            <FaCalendarAlt className="mr-1 text-gray-600" />
                            {trail.bestSeasons.map(season => (
                              <span key={season} className="ml-1">{renderSeasonIcon(season)}</span>
                            ))}
                          </span>
                        )}
                      </div>
                      {trail.description && (
                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">{trail.description}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <FaSearch className="mx-auto text-3xl mb-3 text-gray-300" />
              {translate('trailDiscovery.startSearching', 'Start typing to search for trails')}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 mr-2 hover:bg-gray-50"
          >
            {translate('trailDiscovery.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedTrail}
            className={`px-4 py-2 rounded-md ${selectedTrail
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
          >
            {translate('trailDiscovery.selectTrail', 'Select Trail')}
          </button>
        </div>
      </div>
    </div>
  );
}